import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, shareReplay } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class FeatureFlagService {
	private readonly baseUrl = `${environment.apiUrl}features`;
	private cache = new Map<string, Observable<boolean>>();

	constructor(private http: HttpClient) {}

	/**
	 * Checks if feature flag is enabled and caches the result.
	 *
	 * @param featureName - The name of the feature flag to check.
	 * @returns Server response or Observable of last emitted value from cache.
	 */
	isEnabled(featureName: string): Observable<boolean> {
		if (!this.cache.has(featureName)) {
			// refCount: false is intentional. With refCount: true the replay buffer is torn down as soon as
			// the subscriber count hits zero (which happens immediately for take(1) callers and short-lived
			// components/directives), so every consumer re-issues GET /features/... and the cache never
			// serves a value. refCount: false retains the resolved flag for the app session; the cache is
			// reset by the full page reload on client switch, and clearCachedFlag handles explicit eviction.
			this.cache.set(
				featureName,
				this.http.get<boolean>(`${this.baseUrl}/${featureName}`).pipe(shareReplay({ bufferSize: 1, refCount: false }))
			);
		}
		return this.cache.get(featureName);
	}

	isFeatureEnabledForClient(clientId: string, featureName: string): Observable<boolean> {
		// Key the client-scoped cache by clientId + featureName. Keying by featureName alone leaked one
		// client's result to another after a client switch (e.g. showing/hiding the Claire nav entry
		// based on the previously cached client's value).
		const cacheKey = `${clientId}:${featureName}`;
		if (!this.cache.has(cacheKey)) {
			this.cache.set(
				cacheKey,
				this.http
					.get<boolean>(`${this.baseUrl}/clients/${clientId}/is-enabled/${featureName}`)
					// refCount: false so the resolved flag is retained for the app session instead of being
					// refetched on every subscribe. See the note in isEnabled above.
					.pipe(shareReplay({ bufferSize: 1, refCount: false }))
			);
		}
		return this.cache.get(cacheKey);
	}

	/**
	 * Removes a cached feature flag so the next check re-queries the server.
	 * Pass `clientId` to evict a client-scoped entry (keyed by `${clientId}:${featureName}`);
	 * omit it to evict the user-scoped entry keyed by feature name alone.
	 *
	 * @param featureName - The name of the feature flag to evict from the cache.
	 * @param clientId - Optional client id for client-scoped flags.
	 */
	clearCachedFlag(featureName: string, clientId?: string): void {
		this.cache.delete(clientId ? `${clientId}:${featureName}` : featureName);
	}
}
